import type { ConfigRemota, TipoOferecido } from "../types";

export type { ConfigRemota };

/** Chave do cache da última config boa, versionada e por TOKEN — dois relatores no mesmo
    domínio não podem compartilhá-la (a mesma disciplina do storage de chamados). */
const CACHE_PREFIXO = "altbug:cfg:v1:";

/** Deriva a URL de config a partir do endpoint do intake, trocando o último
    segmento: `.../intake/v1/bugs` → `.../intake/v1/config`. Vazio se inválida. */
function urlConfig(endpoint: string): string {
  try {
    const u = new URL(endpoint);
    u.pathname = u.pathname.replace(/[^/]*$/, "config");
    u.search = "";
    return u.href;
  } catch {
    return "";
  }
}

/** Normaliza o corpo do `/config` pela allowlist. Chave desconhecida é descartada. */
function normalizar(corpo: unknown): ConfigRemota | null {
  if (!corpo || typeof corpo !== "object") return null;
  const c = corpo as Record<string, unknown>;
  const out: ConfigRemota = {};
  if (typeof c.cor === "string") out.cor = c.cor;
  if (typeof c.icone === "string") out.icone = c.icone;
  if (typeof c.consoleHabilitado === "boolean") out.consoleHabilitado = c.consoleHabilitado;
  if (typeof c.aceitaAnonimo === "boolean") out.aceitaAnonimo = c.aceitaAnonimo;
  if (typeof c.exigeLogin === "boolean") out.exigeLogin = c.exigeLogin;
  if (typeof c.portalUrl === "string" && c.portalUrl !== "") out.portalUrl = c.portalUrl;
  if (typeof c.rotuloPrincipal === "string" && c.rotuloPrincipal !== "") out.rotuloPrincipal = c.rotuloPrincipal;
  if (typeof c.rotuloSecundario === "string" && c.rotuloSecundario !== "") out.rotuloSecundario = c.rotuloSecundario;
  if (Array.isArray(c.tiposAdicionais)) out.tiposAdicionais = normalizarTipos(c.tiposAdicionais);

  return out;
}

/** Item sem `id` ou sem `label` é descartado: um tipo sem nome não pode ir para um menu
    que o cliente lê, e um sem id não teria o que mandar ao intake. */
function normalizarTipos(lista: unknown[]): TipoOferecido[] {
  const out: TipoOferecido[] = [];
  for (const item of lista) {
    if (!item || typeof item !== "object") continue;
    const t = item as Record<string, unknown>;
    if (typeof t.id === "string" && t.id !== "" && typeof t.label === "string" && t.label !== "") {
      out.push({ id: t.id, label: t.label });
    }
  }

  return out.slice(0, 20);
}

/** Lê a última config boa deste token. Storage indisponível (Safari privado, iframe sem
    acesso) devolve null — o widget cai nos defaults, nunca quebra. */
export function configEmCache(token: string): ConfigRemota | null {
  try {
    const cru = localStorage.getItem(CACHE_PREFIXO + token);

    return cru ? normalizar(JSON.parse(cru)) : null;
  } catch {
    return null;
  }
}

/**
 * Busca a config do relator no intake (por token, header X-Widget-Token). Falha
 * silenciosa (offline, CORS, 4xx, timeout) → **a última config boa deste token**, e só
 * então null. Sem retry (é best-effort na inicialização). Timeout curto p/ não atrasar
 * o botão além do razoável.
 *
 * O cache deixou de ser conveniência no DEV-144: com cor, ícone e rótulos vindo só daqui,
 * um fetch falho sem cache não esconderia apenas o 2º item do menu — deixaria o widget
 * inteiro com a cara default, num site que configurou a sua. Guardamos apenas o que o
 * SERVIDOR mandou (nunca os defaults mesclados), senão o cache envelheceria como se fosse
 * resposta; e a chave é versionada, para mudança de forma do payload invalidar o antigo
 * em vez de o normalizador ter de tolerá-lo para sempre.
 */
export async function buscarConfigRemota(endpoint: string, token: string): Promise<ConfigRemota | null> {
  const url = urlConfig(endpoint);
  if (!url) return configEmCache(token);

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 3000);
  try {
    const res = await fetch(url, {
      method: "GET",
      headers: { "X-Widget-Token": token },
      signal: ctrl.signal,
    });
    if (!res.ok) return configEmCache(token);
    const out = normalizar(await res.json());
    if (!out) return configEmCache(token);
    try {
      localStorage.setItem(CACHE_PREFIXO + token, JSON.stringify(out));
    } catch {
      /* storage cheio ou bloqueado: a config desta sessão vale, só não sobrevive ao reload */
    }

    return out;
  } catch {
    return configEmCache(token);
  } finally {
    clearTimeout(timer);
  }
}
